import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { Navigate } from 'react-router-dom'
import { ACCESS_KEY, REACT_APP_BSO_USER_ID, urlAPI } from '../../../urls'
import { useAppSelector } from '../../../hooks/redux'
import type { User } from '../../../store/slices/userSlice'
import {
    getAdminAllowedStatuses,
    getLandlordServiceAdminContract,
    isAdminRelatedServiceEditable,
    isAdminScheduledStatus,
    isAdminTerminalStatus,
} from './landlordServiceAdminContract'
import { CURRENT_SUBSCRIPTION_PRODUCT_SERVICE_IDS, ServicesConfig, type ServiceConfigItem } from './servicesConfig'
import { countLandlordCapableUsers, getOrderUser, shouldIncludeLandlordServiceOrder } from './landlordServiceOrderFiltering'
import {
    assertSafeOrderRelatedServiceBody,
    assertSafeRootOrderServiceStatusBody,
    buildOrderRelatedServiceUpdateBody,
    buildRootOrderServiceStatusUpdateBody,
    hasLegacyNestedRelatedServices,
    updateOrderRelatedService,
    updateRootOrderServiceStatus,
    type OrderRelatedServicePatch,
    type RootOrderServiceStatusPatch,
} from './orderRelatedServiceUpdates'
import { isConsultationOrder, resolveConsultationDisplay } from './consultationDisplay'
import './LandlordServiceOrdersPage.css'

type LoadStatus = 'idle' | 'loading' | 'success' | 'error'
type TaskBucket = 'active' | 'scheduled' | 'canceled' | 'completed'

interface ServiceDraft {
    status: string
    notify: boolean
    scheduledAtInput: string
}

interface ResponsibilityChecks {
    reviewedServiceContext: boolean
    confirmedNextStatus: boolean
    acknowledgedLiveImpact: boolean
    acceptedResponsibility: boolean
}

interface AdminServiceIndexes {
    byId: Map<string, ServiceConfigItem>
    byPlanOrderId: Map<string, string>
    byNormalizedTitle: Map<string, string>
}

interface ResolvedServiceIdentity {
    serviceId: string
    isKnown: boolean
    debugLabel?: string
}

interface ClientSummary {
    key: string
    name: string
    email: string
    phone: string
}

interface ClientOrderGroup {
    summary: ClientSummary
    orders: any[]
    unitGroups: UnitOrderGroup[]
    latestTimestamp: number
    counts: {
        active: number
        scheduled: number
        terminal: number
    }
}

export interface LandlordServiceOrderStats {
    totalLandlords: number
    landlordsWithOrders: number
    visibleOrders: number
    active: number
    scheduled: number
    terminal: number
}

interface UnitSummary {
    key: string
    hasUnit: boolean
    propertyName: string
    unitName: string
    location: string
    unitId: string
    thumbnailUrl: string
    shortText: string
}

interface UnitOrderGroup {
    summary: UnitSummary
    orders: any[]
    latestTimestamp: number
}

const toArray = <T,>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : []
const asString = (value: unknown): string => String(value ?? '').trim()
const normalizeText = (value: unknown): string => asString(value).toLowerCase().replace(/^services\./, '').replace(/[^a-z0-9]+/g, ' ').trim()
const normalizeServiceToken = (value: unknown): string => asString(value).toLowerCase().replace(/^services\./, '').replace(/[\s-]+/g, '_')

const buildDefaultResponsibilityChecks = (): ResponsibilityChecks => ({
    reviewedServiceContext: false,
    confirmedNextStatus: false,
    acknowledgedLiveImpact: false,
    acceptedResponsibility: false,
})

const isResponsibilityConfirmed = (checks: ResponsibilityChecks): boolean => Object.values(checks).every(Boolean)

const getEffectiveOrderTimestamp = (order: any): number => {
    const updated = Date.parse(asString(order?.updatedAt))
    if (Number.isFinite(updated)) return updated
    const created = Date.parse(asString(order?.createdAt))
    if (Number.isFinite(created)) return created
    const id = Number(order?.id)
    return Number.isFinite(id) ? id : 0
}

export const buildAdminServiceIndexes = (): AdminServiceIndexes => {
    const byId = new Map<string, ServiceConfigItem>()
    const byPlanOrderId = new Map<string, string>()
    const byNormalizedTitle = new Map<string, string>()

    ServicesConfig.config.forEach((service) => {
        byId.set(service.id, service)
        const titleTokens = [
            service.id,
            service.title,
            service.description,
            ...(service.plans ?? []).flatMap((plan) => [plan.name, plan.title, plan.description]),
        ]
        titleTokens.forEach((token) => {
            const normalized = normalizeText(token)
            if (normalized.length > 0 && !byNormalizedTitle.has(normalized)) byNormalizedTitle.set(normalized, service.id)
        })
        service.plans?.forEach((plan) => {
            const orderId = Number(plan.orderID)
            if (Number.isFinite(orderId)) byPlanOrderId.set(String(orderId), service.id)
        })
    })

    Object.entries(CURRENT_SUBSCRIPTION_PRODUCT_SERVICE_IDS).forEach(([productId, serviceId]) => {
        byPlanOrderId.set(productId, serviceId)
    })

    return { byId, byPlanOrderId, byNormalizedTitle }
}

const SERVICE_INDEXES = buildAdminServiceIndexes()

const serviceExists = (serviceId: string): boolean => SERVICE_INDEXES.byId.has(serviceId)

const resolveKnownServiceId = (value: unknown): string | undefined => {
    const raw = asString(value)
    if (raw.length === 0) return undefined
    const canonical = normalizeServiceToken(raw)
    if (serviceExists(canonical)) return canonical
    if (serviceExists(raw)) return raw
    return undefined
}

const resolvePlanOrderId = (value: unknown): string | undefined => {
    const raw = asString(value)
    if (raw.length === 0) return undefined
    const asNumber = Number(raw)
    if (!Number.isFinite(asNumber)) return undefined
    return SERVICE_INDEXES.byPlanOrderId.get(String(asNumber))
}

export const resolveRootServiceIdFromOrder = (order: any): ResolvedServiceIdentity => {
    if (isConsultationOrder(order)) return { serviceId: 'service_consultation_request', isKnown: true }

    const fromServiceRef = resolveKnownServiceId(order?.ServiceRef)
    if (fromServiceRef != null) return { serviceId: fromServiceRef, isKnown: true }

    const rawProductId = order?.ProductID ?? order?.ProductId ?? order?.productID ?? order?.orderID ?? order?.OrderID
    const fromProductId = resolvePlanOrderId(rawProductId)
    if (fromProductId != null) return { serviceId: fromProductId, isKnown: true, debugLabel: `ProductID ${String(rawProductId)}` }

    for (const relatedService of toArray<any>(order?.RelatedServices)) {
        const fromRelatedId = resolveKnownServiceId(relatedService?.id)
        if (fromRelatedId != null) return { serviceId: fromRelatedId, isKnown: true, debugLabel: `RelatedServices.id ${String(relatedService.id)}` }
    }

    const fallback = normalizeServiceToken(order?.ServiceRef ?? rawProductId)
    return { serviceId: fallback || 'unknown', isKnown: false, debugLabel: asString(rawProductId || fallback || 'unknown') }
}

export const resolveRelatedServiceId = (item: any): ResolvedServiceIdentity => {
    const rawId = typeof item?.id === 'string' ? item.id : undefined
    const fromId = resolveKnownServiceId(rawId)
    if (fromId != null) return { serviceId: fromId, isKnown: true }

    const fallback = normalizeServiceToken(rawId)
    return { serviceId: fallback || 'unknown', isKnown: false, debugLabel: asString(rawId ?? 'unknown') }
}

export const resolveServiceTitle = (serviceId: string, fallbackItem?: any): string => {
    const contract = getLandlordServiceAdminContract(serviceId)
    const service = SERVICE_INDEXES.byId.get(serviceId)
    const fallbackTitle = asString(fallbackItem?.Name ?? fallbackItem?.Package ?? fallbackItem?.Title)
    return contract?.title ?? service?.title ?? fallbackTitle ?? 'Unknown service'
}

const resolveRootOrderTitle = (order: any, serviceId: string): string => {
    return isConsultationOrder(order)
        ? resolveConsultationDisplay(order).title
        : resolveServiceTitle(serviceId, toArray<any>(order?.Service)[0])
}

const unwrapUnit = (order: any): any => order?.Unit?.data?.attributes ?? order?.Unit?.attributes ?? order?.Unit ?? {}

const getClientKey = (order: any): string => {
    const user = getOrderUser(order)
    const contact = user?.ContactInfo ?? {}
    const userId = user?.id ?? order?.User?.data?.id ?? order?.User?.id
    const email = asString(contact?.Email ?? user?.email).toLowerCase()
    const phone = asString(contact?.Phone ?? user?.phone)
    if (userId != null) return `user:${String(userId)}`
    if (email) return `email:${email}`
    if (phone) return `phone:${phone}`
    return 'unknown-client'
}

const getClientSummary = (order: any): ClientSummary => {
    const user = getOrderUser(order)
    const contact = user?.ContactInfo ?? {}
    const name = [contact?.FirstName ?? user?.FirstName, contact?.FamilyName ?? user?.FamilyName].filter(Boolean).join(' ').trim()
    const email = asString(contact?.Email ?? user?.email)
    const phone = asString(contact?.Phone ?? user?.phone)
    return {
        key: getClientKey(order),
        name: name || user?.username || email || phone || 'Unknown client',
        email,
        phone,
    }
}

const getUnitText = (order: any): string => {
    const unit = unwrapUnit(order)
    const property = unit?.Property?.data?.attributes ?? unit?.Property?.attributes ?? unit?.Property ?? {}
    return [property?.Name, property?.Area ?? property?.Community, unit?.Number].filter(Boolean).join(', ') || 'Unit not set'
}

const unwrapMediaUrl = (value: any): string => {
    if (typeof value === 'string') return value
    if (Array.isArray(value)) return value.map(unwrapMediaUrl).find(Boolean) ?? ''
    const item = value?.data?.attributes ?? value?.attributes ?? value
    return asString(item?.url ?? item?.URL ?? item?.src ?? item?.Src ?? item?.formats?.thumbnail?.url ?? item?.formats?.small?.url)
}

const getAbsoluteMediaUrl = (value: string): string => {
    if (!value) return ''
    if (/^https?:\/\//i.test(value)) return value
    const origin = urlAPI.replace(/\/api\/?$/i, '').replace(/\/$/, '')
    return `${origin}${value.startsWith('/') ? '' : '/'}${value}`
}

const getUnitSummary = (order: any): UnitSummary => {
    const unitWrapper = order?.Unit
    const unit = unwrapUnit(order)
    const property = unit?.Property?.data?.attributes ?? unit?.Property?.attributes ?? unit?.Property ?? {}
    const unitId = asString(unitWrapper?.data?.id ?? unitWrapper?.id ?? unit?.id ?? order?.UnitId ?? order?.UnitID)
    const hasUnit = unitId.length > 0 || Object.keys(unit ?? {}).length > 0
    const propertyName = asString(property?.Name ?? property?.Title ?? property?.name)
    const unitName = asString(unit?.Number ?? unit?.UnitNumber ?? unit?.Name ?? unit?.Title)
    const location = [property?.Community, property?.Area, property?.Location, property?.City].map(asString).filter(Boolean).join(', ')
    const rawThumbnail = unwrapMediaUrl(unit?.Thumbnail ?? unit?.Image ?? unit?.Images ?? unit?.Gallery ?? unit?.Photos)
        || unwrapMediaUrl(property?.Thumbnail ?? property?.Image ?? property?.Images ?? property?.Gallery ?? property?.Photos)
    const shortText = hasUnit
        ? [propertyName, unitName].filter(Boolean).join(' / ') || `Unit ${unitId}`
        : 'No unit assigned'

    return {
        key: hasUnit ? `unit:${unitId || normalizeText(shortText)}` : 'no-unit',
        hasUnit,
        propertyName,
        unitName,
        location,
        unitId,
        thumbnailUrl: getAbsoluteMediaUrl(rawThumbnail),
        shortText,
    }
}

const getPaymentText = (order: any): string => {
    return [order?.InvoiceStatus ?? order?.PaymentStatus, order?.InvoiceRefNumber ?? order?.InvoiceId, order?.Amount].filter(Boolean).join(' / ')
}

const formatDateTime = (value: unknown): string => {
    if (typeof value !== 'string' || value.trim().length === 0) return 'Not set'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
}

const toDateTimeInputValue = (iso?: string): string => {
    if (iso == null || iso.trim().length === 0) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (value: number): string => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const toScheduledIso = (input: string): string => new Date(input).toISOString()

const getRelatedItemStatus = (item: any): string => asString(item?.Status ?? item?.ServiceStatus ?? item?.Statuses?.[0])
const getPrimaryOrderStatus = (order: any): string => asString(toArray<string>(order?.Statuses)[0])
const getRootStatus = (order: any): string => {
    const identity = resolveRootServiceIdFromOrder(order)
    return identity.serviceId === 'find_tenant'
        ? getPrimaryOrderStatus(order) || asString(order?.ServiceStatus)
        : asString(order?.ServiceStatus) || getPrimaryOrderStatus(order)
}
const getRootStatuses = (order: any): string => toArray<string>(order?.Statuses).join(', ') || 'Not set'
const isUnpaidOrder = (order: any): boolean => {
    return Array.isArray(order?.Statuses) && order.Statuses.includes("Unpaid")
}

const getStatusTone = (serviceId: string, status: string, scheduledAt?: string): string => {
    const normalized = status.toLowerCase()
    if (normalized.includes('cancel') || normalized.includes('fail') || normalized.includes('expired')) return 'danger'
    if (isAdminTerminalStatus(serviceId, status) || normalized.includes('complete') || normalized.includes('ready') || normalized === 'signed') return 'success'
    if (scheduledAt || isAdminScheduledStatus(serviceId, status) || normalized.includes('scheduled')) return 'warning'
    return 'primary'
}

const getTaskBucket = (serviceId: string, status: string, scheduledAt?: string): TaskBucket => {
    const normalized = status.toLowerCase()
    if (normalized.includes('cancel')) return 'canceled'
    if (isAdminTerminalStatus(serviceId, status) || normalized.includes('complete')) return 'completed'
    if (scheduledAt || isAdminScheduledStatus(serviceId, status) || normalized.includes('scheduled')) return 'scheduled'
    return 'active'
}

const isRootOrderTerminalForDisplay = (order: any, serviceId: string): boolean => {
    const status = getRootStatus(order)
    if (status.length === 0) return false
    if (isAdminTerminalStatus(serviceId, status)) return true
    const normalized = status.toLowerCase()
    return normalized.includes('cancel')
        || normalized.includes('complete')
        || normalized.includes('reject')
        || normalized.includes('expired')
        || normalized.includes('failed')
}

const compareOrdersForAdminDisplay = (a: any, b: any): number => {
    const aIdentity = resolveRootServiceIdFromOrder(a)
    const bIdentity = resolveRootServiceIdFromOrder(b)
    if (aIdentity.serviceId === 'find_tenant' && bIdentity.serviceId === 'find_tenant') {
        const aTerminal = isRootOrderTerminalForDisplay(a, aIdentity.serviceId)
        const bTerminal = isRootOrderTerminalForDisplay(b, bIdentity.serviceId)
        if (aTerminal !== bTerminal) return aTerminal ? 1 : -1
    }

    const timestampDiff = getEffectiveOrderTimestamp(b) - getEffectiveOrderTimestamp(a)
    if (timestampDiff !== 0) return timestampDiff
    return Number(b?.id ?? 0) - Number(a?.id ?? 0)
}

async function api(payload: any): Promise<any> {
    const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
    const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
    })
    const json = await response.json()
    if (!json?.success || json?.response?.error) {
        throw new Error(JSON.stringify(json?.response?.error ?? json))
    }
    return json.response
}

const ORDER_PAGE_SIZE = 100

interface OrdersPage {
    rows: any[]
    pageCount?: number
}

const getOrderPage = async (page: number): Promise<OrdersPage> => {
    const query = [
        `pagination[page]=${page}`,
        `pagination[pageSize]=${ORDER_PAGE_SIZE}`,
        'pagination[withCount]=true',
        'sort[0]=updatedAt:desc',
        'populate[Service]=*',
        'populate[RelatedServices]=*',
        'populate[User][fields][0]=Category',
        'populate[User][populate][ContactInfo]=*',
        'populate[Unit][populate]=*',
    ].join('&')
    const response = await api({ method: 'GET', collection: 'orders', query })
    const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
    const pageCount = Number(response?.meta?.pagination?.pageCount)
    return {
        rows: rows.map((row: any) => row?.attributes != null ? { id: row.id, ...row.attributes } : row),
        pageCount: Number.isFinite(pageCount) && pageCount > 0 ? pageCount : undefined,
    }
}

export const loadAllOrderPages = async (
    loadPage: (page: number) => Promise<OrdersPage>,
    pageSize = ORDER_PAGE_SIZE,
): Promise<any[]> => {
    const orders: any[] = []
    const seenOrderIds = new Set<string>()
    let page = 1
    let knownPageCount: number | undefined

    while (true) {
        const result = await loadPage(page)
        if (result.pageCount != null) knownPageCount = result.pageCount

        result.rows.forEach((order) => {
            const key = String(order?.id ?? `${page}:${orders.length}`)
            if (seenOrderIds.has(key)) return
            seenOrderIds.add(key)
            orders.push(order)
        })

        if (knownPageCount != null && page >= knownPageCount) return orders
        // A short or empty page is the safe fallback for deployments that omit pagination metadata.
        if (result.rows.length < pageSize) return orders
        page += 1
    }
}

export const fetchOrders = async (): Promise<any[]> => {
    return await loadAllOrderPages(getOrderPage)
}

const getRootInstanceKey = (orderId: number | string, serviceId: string): string => `${orderId}:root:${serviceId}`

const getRelatedInstanceKey = (orderId: number | string, item: any, index: number): string => {
    const identity = resolveRelatedServiceId(item)
    return `${orderId}:related:${index}:${identity.serviceId}`
}

const getSourceDraft = (item: any): ServiceDraft => ({
    status: getRelatedItemStatus(item),
    notify: item?.Notify === true,
    scheduledAtInput: toDateTimeInputValue(item?.ScheduledAt),
})

const getRootSourceDraft = (order: any): ServiceDraft => ({
    status: getRootStatus(order),
    notify: order?.Notify === true,
    scheduledAtInput: toDateTimeInputValue(order?.ScheduledAt),
})

const isRootStatusLockedByTerminalState = (order: any, serviceId: string): boolean => {
    const rootStatus = getRootStatus(order)
    if (rootStatus.length === 0) return false
    if (isAdminTerminalStatus(serviceId, rootStatus)) return true
    const normalized = rootStatus.toLowerCase()
    return normalized.includes('cancel')
        || normalized.includes('complete')
        || normalized.includes('reject')
        || normalized.includes('expired')
        || normalized.includes('failed')
}

const canEditRootServiceStatus = (order: any, identity: ResolvedServiceIdentity): boolean => {
    if (!identity.isKnown) return false
    const contract = getLandlordServiceAdminContract(identity.serviceId)
    if (contract?.updateTarget === 'order_root' && contract.canUpdateRootOrder === true) return true

    // Allow standalone related-service orders to be edited from root when they have no top-level RelatedServices.
    const standaloneRelatedOrder = contract?.updateTarget === 'order_related_services'
        && contract.canUpdateRelatedService === true
        && toArray<any>(order?.RelatedServices).length === 0
        && !hasLegacyNestedRelatedServices(order)

    if (!standaloneRelatedOrder) return false
    return !isRootStatusLockedByTerminalState(order, identity.serviceId)
}

const hasDraftChanged = (draft: ServiceDraft, source: ServiceDraft, serviceId: string): boolean => {
    const scheduleChanged = isAdminScheduledStatus(serviceId, draft.status) && draft.scheduledAtInput !== source.scheduledAtInput
    return draft.status !== source.status || draft.notify !== source.notify || scheduleChanged
}

const buildRelatedPatchFromDraft = (draft: ServiceDraft, serviceId: string): OrderRelatedServicePatch => {
    const patch: OrderRelatedServicePatch = {
        Status: draft.status,
        Notify: draft.notify,
        updatedAt: new Date().toISOString(),
    }
    if (isAdminScheduledStatus(serviceId, draft.status)) patch.ScheduledAt = toScheduledIso(draft.scheduledAtInput)
    return patch
}

const buildRootPatchFromDraft = (order: any, draft: ServiceDraft, serviceId: string): RootOrderServiceStatusPatch => {
    const patch: RootOrderServiceStatusPatch = {
        Notify: draft.notify,
        updatedAt: new Date().toISOString(),
    }

    if (serviceId === 'find_tenant') {
        patch.Statuses = [draft.status]
        if (draft.status === 'Completed' || draft.status === 'Canceled') patch.ServiceStatus = draft.status
    } else {
        patch.Statuses = ['Custom stage']
        patch.ServiceStatus = draft.status
    }
    if (isAdminScheduledStatus(serviceId, draft.status)) patch.ScheduledAt = toScheduledIso(draft.scheduledAtInput)

    return patch
}

const groupOrdersByClient = (orders: any[]): ClientOrderGroup[] => {
    const groups = new Map<string, ClientOrderGroup>()
    orders.forEach((order) => {
        const summary = getClientSummary(order)
        const current = groups.get(summary.key)
        if (current == null) groups.set(summary.key, { summary, orders: [order], unitGroups: [], latestTimestamp: getEffectiveOrderTimestamp(order), counts: { active: 0, scheduled: 0, terminal: 0 } })
        else {
            current.orders.push(order)
            current.latestTimestamp = Math.max(current.latestTimestamp, getEffectiveOrderTimestamp(order))
        }
    })

    groups.forEach((group) => {
        const counts = { active: 0, scheduled: 0, terminal: 0 }
        const unitGroups = new Map<string, UnitOrderGroup>()
        group.orders.sort(compareOrdersForAdminDisplay)
        group.orders.forEach((order) => {
            const unitSummary = getUnitSummary(order)
            const orderTimestamp = getEffectiveOrderTimestamp(order)
            const unitGroup = unitGroups.get(unitSummary.key)
            if (unitGroup == null) unitGroups.set(unitSummary.key, { summary: unitSummary, orders: [order], latestTimestamp: orderTimestamp })
            else {
                unitGroup.orders.push(order)
                unitGroup.latestTimestamp = Math.max(unitGroup.latestTimestamp, orderTimestamp)
            }

            const rootIdentity = resolveRootServiceIdFromOrder(order)
            const rootBucket = getTaskBucket(rootIdentity.serviceId, getRootStatus(order), order?.ScheduledAt)
            if (rootBucket === 'active') counts.active += 1
            else if (rootBucket === 'scheduled') counts.scheduled += 1
            else counts.terminal += 1

            toArray<any>(order?.RelatedServices).forEach((item) => {
                const relatedIdentity = resolveRelatedServiceId(item)
                const bucket = getTaskBucket(relatedIdentity.serviceId, getRelatedItemStatus(item), item?.ScheduledAt)
                if (bucket === 'active') counts.active += 1
                else if (bucket === 'scheduled') counts.scheduled += 1
                else counts.terminal += 1
            })
        })
        group.unitGroups = Array.from(unitGroups.values())
            .map((unitGroup) => ({
                ...unitGroup,
                orders: unitGroup.orders.sort(compareOrdersForAdminDisplay),
            }))
            .sort((a, b) => b.latestTimestamp - a.latestTimestamp)
        group.counts = counts
    })

    return Array.from(groups.values()).sort((a, b) => b.latestTimestamp - a.latestTimestamp)
}

const getUniqueOrdersById = (orders: any[]): any[] => {
    const seen = new Set<string>()
    return orders.filter((order, index) => {
        const key = String(order?.id ?? `index:${String(index)}`)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

const buildLandlordServiceOrderStatsFromGroups = (groups: ClientOrderGroup[], totalLandlords: number): LandlordServiceOrderStats => {
    return {
        totalLandlords,
        landlordsWithOrders: groups.length,
        visibleOrders: groups.reduce((total, group) => total + group.orders.length, 0),
        active: groups.reduce((total, group) => total + group.counts.active, 0),
        scheduled: groups.reduce((total, group) => total + group.counts.scheduled, 0),
        terminal: groups.reduce((total, group) => total + group.counts.terminal, 0),
    }
}

export const buildLandlordServiceOrderStats = (orders: any[], totalLandlords: number): LandlordServiceOrderStats => (
    buildLandlordServiceOrderStatsFromGroups(groupOrdersByClient(getUniqueOrdersById(orders)), totalLandlords)
)

function ResponsibilityConfirmation({ checks, onChange }: { checks: ResponsibilityChecks, onChange: (checks: ResponsibilityChecks) => void }): JSX.Element {
    const setCheck = (key: keyof ResponsibilityChecks, value: boolean): void => onChange({ ...checks, [key]: value })
    return (
        <div className="border border-warning rounded p-2 bg-white mt-2">
            <div className="text-dark fs-8 fw-semibold mb-1">Responsibility confirmation (required to save)</div>
            <div className="d-flex flex-column gap-1">
                <label className="form-check form-check-custom form-check-solid">
                    <input className="form-check-input" type="checkbox" checked={checks.reviewedServiceContext} onChange={(event) => setCheck('reviewedServiceContext', event.target.checked)} />
                    <span className="form-check-label text-gray-700 fs-8">I reviewed this service context.</span>
                </label>
                <label className="form-check form-check-custom form-check-solid">
                    <input className="form-check-input" type="checkbox" checked={checks.confirmedNextStatus} onChange={(event) => setCheck('confirmedNextStatus', event.target.checked)} />
                    <span className="form-check-label text-gray-700 fs-8">I confirm the selected next status.</span>
                </label>
                <label className="form-check form-check-custom form-check-solid">
                    <input className="form-check-input" type="checkbox" checked={checks.acknowledgedLiveImpact} onChange={(event) => setCheck('acknowledgedLiveImpact', event.target.checked)} />
                    <span className="form-check-label text-gray-700 fs-8">I understand this updates live operational flow.</span>
                </label>
                <label className="form-check form-check-custom form-check-solid">
                    <input className="form-check-input" type="checkbox" checked={checks.acceptedResponsibility} onChange={(event) => setCheck('acceptedResponsibility', event.target.checked)} />
                    <span className="form-check-label text-gray-700 fs-8">I accept responsibility for this update.</span>
                </label>
            </div>
            {!isResponsibilityConfirmed(checks) && <div className="text-warning fs-8 mt-1">Complete all confirmations to enable Save status.</div>}
        </div>
    )
}

function ServiceBadges({ identity, status, scheduledAt, notify, updatedAt, editable }: {
    identity: ResolvedServiceIdentity
    status: string
    scheduledAt?: string
    notify?: boolean
    updatedAt?: string
    editable?: boolean
}): JSX.Element {
    return (
        <div className="lso-badge-row">
            <span className={`badge lso-badge badge-light-${getStatusTone(identity.serviceId, status, scheduledAt)}`}>{status || 'No status'}</span>
            {scheduledAt && <span className="badge lso-badge badge-light">Scheduled {formatDateTime(scheduledAt)}</span>}
            <span className={notify ? 'badge lso-badge badge-light-info' : 'badge lso-badge badge-light'}>Notify {notify ? 'On' : 'Off'}</span>
            {updatedAt && <span className="badge lso-badge badge-light">Updated {formatDateTime(updatedAt)}</span>}
            {!identity.isKnown && <span className="badge lso-badge badge-light-danger">Unknown</span>}
            {identity.isKnown && !editable && <span className="badge lso-badge badge-light-warning">Read-only</span>}
        </div>
    )
}

function ChevronIcon({ expanded }: { expanded: boolean }): JSX.Element {
    return (
        <span className={`lso-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
            <span className="lso-chevron-mark">&gt;</span>
        </span>
    )
}

function ServiceStatusEditor({
    serviceId,
    draft,
    source,
    saving,
    responsibility,
    onDraftChange,
    onResponsibilityChange,
    onSave,
    onReset,
}: {
    serviceId: string
    draft: ServiceDraft
    source: ServiceDraft
    saving: boolean
    responsibility: ResponsibilityChecks
    onDraftChange: (patch: Partial<ServiceDraft>) => void
    onResponsibilityChange: (checks: ResponsibilityChecks) => void
    onSave: () => void
    onReset: () => void
}): JSX.Element {
    const options = getAdminAllowedStatuses(serviceId)
    const selectedStatusIsAllowed = options.includes(draft.status)
    const requiresSchedule = isAdminScheduledStatus(serviceId, draft.status)
    const changed = hasDraftChanged(draft, source, serviceId)
    const responsibilityConfirmed = isResponsibilityConfirmed(responsibility)
    const scheduleReady = !requiresSchedule || draft.scheduledAtInput.trim().length > 0

    return (
        <div className="lso-status-editor">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-4">
                    <label className="form-label fs-8 text-gray-600">Status</label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedStatusIsAllowed ? draft.status : ''}
                        onChange={(event) => {
                            const nextStatus = event.target.value
                            onDraftChange({
                                status: nextStatus,
                                notify: nextStatus !== source.status ? true : draft.notify,
                            })
                        }}
                    >
                        {!selectedStatusIsAllowed && <option value="" disabled>Select status</option>}
                        {options.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                    {!selectedStatusIsAllowed && source.status && <div className="text-warning fs-8 mt-1">Current status is outside this service contract: {source.status}</div>}
                    {source.scheduledAtInput && <div className="text-gray-600 fs-8 mt-1">ScheduledAt: {formatDateTime(new Date(source.scheduledAtInput).toISOString())}</div>}
                </div>
                {requiresSchedule && (
                    <div className="col-12 col-md-4">
                        <label className="form-label fs-8 text-gray-600">ScheduledAt</label>
                        <input className="form-control form-control-sm" type="datetime-local" value={draft.scheduledAtInput} onChange={(event) => onDraftChange({ scheduledAtInput: event.target.value })} />
                    </div>
                )}
                <div className="col-12 col-md-2">
                    <label className="form-check form-check-custom form-check-solid mb-2">
                        <input className="form-check-input" type="checkbox" checked={draft.notify} onChange={(event) => onDraftChange({ notify: event.target.checked })} />
                        <span className="form-check-label">Notify</span>
                    </label>
                </div>
                <div className="col-12 col-md-2 d-flex gap-2 lso-editor-actions">
                    {changed && (
                        <button className="btn btn-sm btn-primary" type="button" disabled={saving || !scheduleReady || !responsibilityConfirmed} onClick={onSave}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    )}
                    {changed && <button className="btn btn-sm btn-light" type="button" disabled={saving} onClick={onReset}>Reset</button>}
                </div>
            </div>
            {changed && !scheduleReady && <div className="text-danger fs-8 mt-1">ScheduledAt is required for this status.</div>}
            {changed && <ResponsibilityConfirmation checks={responsibility} onChange={onResponsibilityChange} />}
        </div>
    )
}

function RootServiceCard({
    order,
    draft,
    responsibility,
    saving,
    onDraftChange,
    onResponsibilityChange,
    onSave,
    onReset,
}: {
    order: any
    draft: ServiceDraft
    responsibility: ResponsibilityChecks
    saving: boolean
    onDraftChange: (patch: Partial<ServiceDraft>) => void
    onResponsibilityChange: (checks: ResponsibilityChecks) => void
    onSave: () => void
    onReset: () => void
}): JSX.Element {
    const identity = resolveRootServiceIdFromOrder(order)
    const title = resolveRootOrderTitle(order, identity.serviceId)
    const status = getRootStatus(order) || getRootStatuses(order)
    const payment = getPaymentText(order)
    const editable = canEditRootServiceStatus(order, identity)
    const source = getRootSourceDraft(order)

    return (
        <div className="lso-root-card">
            <div className="lso-service-card-header">
                <div className="lso-text-stack">
                    <div className="text-dark lso-service-title lso-line-clamp-2">{title}</div>
                    <div className="fs-8 text-gray-600">Order #{String(order.id)}{identity.isKnown ? '' : ` - ${identity.debugLabel ?? identity.serviceId}`}</div>
                </div>
                <ServiceBadges identity={identity} status={status} scheduledAt={order?.ScheduledAt} notify={order?.Notify === true} editable={editable} />
            </div>
            <div className="lso-meta-row">
                <span>Created {formatDateTime(order?.createdAt)}</span>
                {payment && <span>Payment <span className="fw-semibold text-dark">{payment}</span></span>}
            </div>
            {isConsultationOrder(order) && resolveConsultationDisplay(order).comments && (
                <div className="text-gray-700 fs-8 mt-2">{resolveConsultationDisplay(order).comments}</div>
            )}
            {editable ? (
                <ServiceStatusEditor
                    serviceId={identity.serviceId}
                    draft={draft}
                    source={source}
                    saving={saving}
                    responsibility={responsibility}
                    onDraftChange={onDraftChange}
                    onResponsibilityChange={onResponsibilityChange}
                    onSave={onSave}
                    onReset={onReset}
                />
            ) : (
                <div className="alert alert-light py-2 mt-3 mb-0 fs-8">
                    Root status is read-only for this order.
                </div>
            )}
        </div>
    )
}

function UnsupportedOrderCard({ order }: { order: any }): JSX.Element {
    const identity = resolveRootServiceIdFromOrder(order)
    return (
        <div className="lso-order-card lso-unsupported-card">
            <div className="lso-service-card-header">
                <div className="lso-text-stack">
                    <div className="text-dark lso-service-title lso-line-clamp-2">{asString(order?.Name) || 'Unsupported / external order'}</div>
                    <div className="fs-8 text-gray-600">Order #{String(order.id)} - {identity.debugLabel ?? identity.serviceId}</div>
                </div>
                <div className="lso-badge-row">
                    <span className="badge lso-badge badge-light-danger">Unsupported / external</span>
                    <span className="badge lso-badge badge-light">{getRootStatus(order) || getRootStatuses(order)}</span>
                    <span className="badge lso-badge badge-light">Updated {formatDateTime(order?.updatedAt ?? order?.createdAt)}</span>
                </div>
            </div>
        </div>
    )
}

function RelatedServiceCard({
    order,
    item,
    index,
    legacyWarning,
    draft,
    responsibility,
    saving,
    onDraftChange,
    onResponsibilityChange,
    onSave,
    onReset,
}: {
    order: any
    item: any
    index: number
    legacyWarning: boolean
    draft: ServiceDraft
    responsibility: ResponsibilityChecks
    saving: boolean
    onDraftChange: (patch: Partial<ServiceDraft>) => void
    onResponsibilityChange: (checks: ResponsibilityChecks) => void
    onSave: () => void
    onReset: () => void
}): JSX.Element {
    const identity = resolveRelatedServiceId(item)
    const title = resolveServiceTitle(identity.serviceId, item)
    const editable = identity.isKnown && !legacyWarning && isAdminRelatedServiceEditable(identity.serviceId)
    const source = getSourceDraft(item)

    return (
        <div className="lso-related-card">
            <div className="lso-service-card-header">
                <div className="lso-text-stack">
                    <div className="text-dark lso-service-title lso-line-clamp-2">{title}</div>
                    <div className="fs-8 text-gray-600">{identity.isKnown ? identity.serviceId : `Unknown service - ${identity.debugLabel ?? (identity.serviceId || `related-${index + 1}`)}`}</div>
                </div>
                <ServiceBadges identity={identity} status={getRelatedItemStatus(item)} scheduledAt={item?.ScheduledAt} notify={item?.Notify === true} updatedAt={item?.updatedAt} editable={editable} />
            </div>
            {editable ? (
                <ServiceStatusEditor
                    serviceId={identity.serviceId}
                    draft={draft}
                    source={source}
                    saving={saving}
                    responsibility={responsibility}
                    onDraftChange={onDraftChange}
                    onResponsibilityChange={onResponsibilityChange}
                    onSave={onSave}
                    onReset={onReset}
                />
            ) : (
                <div className="text-gray-600 fs-8">{identity.isKnown ? 'Visible for context and not editable here.' : 'Unknown service is read-only.'}</div>
            )}
        </div>
    )
}

function UnitSummaryBlock({ summary }: { summary: UnitSummary }): JSX.Element {
    if (!summary.hasUnit) {
        return (
            <div className="lso-unit-summary lso-unit-summary-no-unit">
                <div className="lso-unit-thumb lso-unit-thumb-empty">NU</div>
                <div className="lso-text-stack">
                    <div className="lso-unit-title">Orders without unit</div>
                    <div className="lso-unit-subtitle">Bitrix / no-unit orders</div>
                    <div className="lso-unit-meta">No property linkage assigned</div>
                </div>
            </div>
        )
    }

    return (
        <div className="lso-unit-summary">
            {summary.thumbnailUrl ? (
                <img src={summary.thumbnailUrl} alt="" className="lso-unit-thumb" />
            ) : (
                <div className="lso-unit-thumb lso-unit-thumb-empty">Unit</div>
            )}
            <div className="lso-text-stack">
                <div className="lso-unit-title lso-line-clamp-2">{summary.propertyName || 'Property not set'}</div>
                <div className="lso-unit-subtitle lso-line-clamp-1">{summary.unitName || 'Unit not set'}</div>
                {summary.location && <div className="lso-unit-meta lso-line-clamp-1">{summary.location}</div>}
                {summary.unitId && <div className="lso-unit-meta">Unit ID: {summary.unitId}</div>}
            </div>
        </div>
    )
}

function OrderServiceTreeCard({
    order,
    unitSummary,
    drafts,
    responsibilities,
    savingKey,
    collapsed,
    relatedCollapsed,
    onToggleCollapsed,
    onToggleRelatedCollapsed,
    onDraftChange,
    onResponsibilityChange,
    onResetDraft,
    onSaveRoot,
    onSaveRelated,
}: {
    order: any
    unitSummary: UnitSummary
    drafts: Record<string, ServiceDraft>
    responsibilities: Record<string, ResponsibilityChecks>
    savingKey: string
    collapsed: boolean
    relatedCollapsed: boolean
    onToggleCollapsed: () => void
    onToggleRelatedCollapsed: () => void
    onDraftChange: (key: string, source: ServiceDraft, patch: Partial<ServiceDraft>) => void
    onResponsibilityChange: (key: string, checks: ResponsibilityChecks) => void
    onResetDraft: (key: string) => void
    onSaveRoot: (order: any, key: string) => void
    onSaveRelated: (order: any, item: any, index: number, key: string) => void
}): JSX.Element {
    const rootIdentity = resolveRootServiceIdFromOrder(order)
    const rootTitle = resolveRootOrderTitle(order, rootIdentity.serviceId)
    const legacyWarning = hasLegacyNestedRelatedServices(order)
    const relatedServices = toArray<any>(order?.RelatedServices)
    const rootKey = getRootInstanceKey(order.id, rootIdentity.serviceId)
    const rootSource = getRootSourceDraft(order)
    const rootDraft = drafts[rootKey] ?? rootSource

    if (!rootIdentity.isKnown) return <UnsupportedOrderCard order={order} />

    return (
        <div className={`lso-order-card${collapsed ? ' is-collapsed' : ''}`}>
            <div
                className="lso-order-header"
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                onClick={onToggleCollapsed}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onToggleCollapsed()
                    }
                }}
            >
                <div className="lso-order-title-area">
                    <ChevronIcon expanded={!collapsed} />
                    <div className="lso-text-stack">
                        <div className="lso-order-title lso-line-clamp-2">{rootTitle}</div>
                        <div className="lso-order-meta lso-line-clamp-1">Order #{String(order.id)} - Updated {formatDateTime(order?.updatedAt)} - {unitSummary.shortText}</div>
                    </div>
                </div>
                <div className="lso-badge-row lso-order-status-row">
                    <span className={`badge lso-badge badge-light-${getStatusTone(rootIdentity.serviceId, getRootStatus(order), order?.ScheduledAt)}`}>{getRootStatus(order) || getRootStatuses(order)}</span>
                </div>
            </div>
            {!collapsed && <div className="lso-order-body">
                {legacyWarning && <div className="alert alert-warning py-2 mb-3">Legacy nested related services detected. Migration required.</div>}
                <div className="d-flex flex-column gap-3">
                    <RootServiceCard
                        order={order}
                        draft={rootDraft}
                        responsibility={responsibilities[rootKey] ?? buildDefaultResponsibilityChecks()}
                        saving={savingKey === rootKey}
                        onDraftChange={(patch) => onDraftChange(rootKey, rootSource, patch)}
                        onResponsibilityChange={(checks) => onResponsibilityChange(rootKey, checks)}
                        onSave={() => onSaveRoot(order, rootKey)}
                        onReset={() => onResetDraft(rootKey)}
                    />
                    <div className="lso-related-section">
                        {relatedServices.length > 0 ? (
                            <>
                                <div className="lso-related-heading">
                                    <div className="fs-8 fw-semibold text-gray-600 text-uppercase">Related services</div>
                                    {relatedServices.length > 3 && (
                                        <button className="btn btn-sm btn-light" type="button" onClick={onToggleRelatedCollapsed}>
                                            {relatedCollapsed ? `Show ${relatedServices.length}` : 'Hide'}
                                        </button>
                                    )}
                                </div>
                                {(!relatedCollapsed || relatedServices.length <= 3) && relatedServices.map((item, index) => {
                                    const key = getRelatedInstanceKey(order.id, item, index)
                                    const source = getSourceDraft(item)
                                    const draft = drafts[key] ?? source
                                    return (
                                        <RelatedServiceCard
                                            key={key}
                                            order={order}
                                            item={item}
                                            index={index}
                                            legacyWarning={legacyWarning}
                                            draft={draft}
                                            responsibility={responsibilities[key] ?? buildDefaultResponsibilityChecks()}
                                            saving={savingKey === key}
                                            onDraftChange={(patch) => onDraftChange(key, source, patch)}
                                            onResponsibilityChange={(checks) => onResponsibilityChange(key, checks)}
                                            onSave={() => onSaveRelated(order, item, index, key)}
                                            onReset={() => onResetDraft(key)}
                                        />
                                    )
                                })}
                            </>
                        ) : (
                            <div className="fs-8 text-gray-600">No related services for this order.</div>
                        )}
                    </div>
                </div>
            </div>}
        </div>
    )
}

export function ClientOrderGroupCard({
    group,
    drafts,
    responsibilities,
    savingKey,
    collapsedKeys,
    collapsedUserIds,
    onSetCollapsed,
    onToggleUser,
    onDraftChange,
    onResponsibilityChange,
    onResetDraft,
    onSaveRoot,
    onSaveRelated,
}: {
    group: ClientOrderGroup
    drafts: Record<string, ServiceDraft>
    responsibilities: Record<string, ResponsibilityChecks>
    savingKey: string
    collapsedKeys: Record<string, boolean>
    collapsedUserIds: Set<string>
    onSetCollapsed: (key: string, collapsed: boolean) => void
    onToggleUser: (userKey: string) => void
    onDraftChange: (key: string, source: ServiceDraft, patch: Partial<ServiceDraft>) => void
    onResponsibilityChange: (key: string, checks: ResponsibilityChecks) => void
    onResetDraft: (key: string) => void
    onSaveRoot: (order: any, key: string) => void
    onSaveRelated: (order: any, item: any, index: number, key: string) => void
}): JSX.Element {
    const userCollapsed = collapsedUserIds.has(group.summary.key)
    return (
        <div className="lso-client-card">
            <div
                className="lso-client-header"
                role="button"
                tabIndex={0}
                aria-expanded={!userCollapsed}
                onClick={() => onToggleUser(group.summary.key)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onToggleUser(group.summary.key)
                    }
                }}
            >
                <div className="lso-text-stack">
                    <div className="d-flex align-items-center gap-2">
                        <ChevronIcon expanded={!userCollapsed} />
                        <div className="fw-bold fs-5 text-dark">{group.summary.name}</div>
                    </div>
                    <div className="lso-client-contact">
                        {group.summary.email && <span>{group.summary.email}</span>}
                        {group.summary.phone && <span>{group.summary.phone}</span>}
                        {!group.summary.email && !group.summary.phone && <span>Contact not set</span>}
                    </div>
                </div>
                <div className="lso-badge-row lso-client-badges">
                    <span className="badge lso-badge badge-light-primary">{group.orders.length} orders</span>
                    <span className="badge lso-badge badge-light-info">{group.counts.active} active</span>
                    <span className="badge lso-badge badge-light-warning">{group.counts.scheduled} scheduled</span>
                    <span className="badge lso-badge badge-light-success">{group.counts.terminal} terminal</span>
                </div>
            </div>
            {!userCollapsed && <div className="lso-client-body">
                <div className="lso-unit-list">
                    {group.unitGroups.map((unitGroup, unitIndex) => {
                        const unitCollapseKey = `${group.summary.key}:${unitGroup.summary.key}`
                        const unitCollapsed = collapsedKeys[unitCollapseKey] ?? unitIndex > 0
                        return (
                            <div key={unitGroup.summary.key} className={`lso-unit-card${unitGroup.summary.hasUnit ? '' : ' lso-unit-card-no-unit'}${unitCollapsed ? ' is-collapsed' : ''}`}>
                                <div
                                    className="lso-unit-header"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={!unitCollapsed}
                                    onClick={() => onSetCollapsed(unitCollapseKey, !unitCollapsed)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault()
                                            onSetCollapsed(unitCollapseKey, !unitCollapsed)
                                        }
                                    }}
                                >
                                    <UnitSummaryBlock summary={unitGroup.summary} />
                                    <div className="lso-unit-actions">
                                        <div className="lso-unit-badge-column">
                                            <span className="badge lso-badge badge-light-primary">{unitGroup.orders.length} orders</span>
                                            <span className="badge lso-badge badge-light">Updated {formatDateTime(new Date(unitGroup.latestTimestamp).toISOString())}</span>
                                        </div>
                                        <ChevronIcon expanded={!unitCollapsed} />
                                    </div>
                                </div>
                                {!unitCollapsed && (
                                    <div className="lso-order-list">
                                        {unitGroup.orders.map((order, orderIndex) => {
                                            const orderCollapseKey = `${unitCollapseKey}:order:${String(order.id)}`
                                            const relatedCollapseKey = `${orderCollapseKey}:related`
                                            const orderCollapsed = collapsedKeys[orderCollapseKey] ?? orderIndex > 0
                                            const relatedCollapsed = collapsedKeys[relatedCollapseKey] ?? toArray<any>(order?.RelatedServices).length > 3
                                            return (
                                                <OrderServiceTreeCard
                                                    key={String(order.id)}
                                                    order={order}
                                                    unitSummary={unitGroup.summary}
                                                    drafts={drafts}
                                                    responsibilities={responsibilities}
                                                    savingKey={savingKey}
                                                    collapsed={orderCollapsed}
                                                    relatedCollapsed={relatedCollapsed}
                                                    onToggleCollapsed={() => onSetCollapsed(orderCollapseKey, !orderCollapsed)}
                                                    onToggleRelatedCollapsed={() => onSetCollapsed(relatedCollapseKey, !relatedCollapsed)}
                                                    onDraftChange={onDraftChange}
                                                    onResponsibilityChange={onResponsibilityChange}
                                                    onResetDraft={onResetDraft}
                                                    onSaveRoot={onSaveRoot}
                                                    onSaveRelated={onSaveRelated}
                                                />
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>}
        </div>
    )
}

export default function LandlordServiceOrdersPage(): JSX.Element {
    const currentUser = useAppSelector((state) => state.user.currentUser as User | undefined)
    const allUsers = useAppSelector((state) => state.user.allUsers as any[])
    const isAdmin = currentUser?.id === Number(REACT_APP_BSO_USER_ID)
    const [orders, setOrders] = useState<any[]>([])
    const [drafts, setDrafts] = useState<Record<string, ServiceDraft>>({})
    const [responsibilities, setResponsibilities] = useState<Record<string, ResponsibilityChecks>>({})
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('idle')
    const [error, setError] = useState<string>('')
    const [savingKey, setSavingKey] = useState<string>('')
    const [search, setSearch] = useState<string>('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [serviceFilter, setServiceFilter] = useState<string>('all')
    const [collapsedKeys, setCollapsedKeys] = useState<Record<string, boolean>>({})
    const [collapsedUserIds, setCollapsedUserIds] = useState<Set<string>>(() => new Set())

    const loadOrders = async (): Promise<void> => {
        setLoadStatus('loading')
        setError('')
        try {
            setOrders(await fetchOrders())
            setLoadStatus('success')
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load orders.')
            setLoadStatus('error')
        }
    }

    useEffect(() => {
        void loadOrders()
    }, [])

    const visibleOrders = useMemo(() => (
        orders.filter((order) => !isUnpaidOrder(order) && shouldIncludeLandlordServiceOrder(order))
    ), [orders])
    const visibleLandlordCount = useMemo(() => new Set(visibleOrders.map(getClientKey)).size, [visibleOrders])

    const serviceFilterOptions = useMemo(() => {
        const observed = new Set<string>()
        const unknown = new Map<string, string>()
        visibleOrders.forEach((order) => {
            const rootIdentity = resolveRootServiceIdFromOrder(order)
            if (rootIdentity.isKnown) observed.add(rootIdentity.serviceId)
            else unknown.set(rootIdentity.serviceId, rootIdentity.debugLabel ?? rootIdentity.serviceId)
            toArray<any>(order?.RelatedServices).forEach((item) => {
                const identity = resolveRelatedServiceId(item)
                if (identity.isKnown) observed.add(identity.serviceId)
                else unknown.set(identity.serviceId, identity.debugLabel ?? identity.serviceId)
            })
        })

        const knownOptions = Array.from(observed)
            .map((id) => ({ id, title: resolveServiceTitle(id), unknown: false }))
            .sort((a, b) => a.title.localeCompare(b.title))
        const unknownOptions = Array.from(unknown.entries())
            .map(([id, label]) => ({ id, title: `Unknown service: ${label}`, unknown: true }))
            .sort((a, b) => a.title.localeCompare(b.title))
        return [...knownOptions, ...unknownOptions]
    }, [visibleOrders])

    const filteredOrders = useMemo(() => {
        const searchText = normalizeText(search)
        return visibleOrders.filter((order) => {
            const related = toArray<any>(order?.RelatedServices)
            const client = getClientSummary(order)
            const rootIdentity = resolveRootServiceIdFromOrder(order)
            const rootTitle = resolveServiceTitle(rootIdentity.serviceId, toArray<any>(order?.Service)[0])
            const searchableText = normalizeText([
                order?.id,
                order?.Name,
                client.name,
                client.email,
                client.phone,
                getUnitText(order),
                rootIdentity.serviceId,
                rootTitle,
                getRootStatus(order),
                getRootStatuses(order),
                ...related.flatMap((item) => {
                    const identity = resolveRelatedServiceId(item)
                    return [identity.serviceId, resolveServiceTitle(identity.serviceId, item), getRelatedItemStatus(item)]
                }),
            ].join(' '))

            if (searchText.length > 0 && !searchableText.includes(searchText)) return false
            if (serviceFilter !== 'all' && rootIdentity.serviceId !== serviceFilter && !related.some((item) => resolveRelatedServiceId(item).serviceId === serviceFilter)) return false
            if (statusFilter !== 'all') {
                const buckets = [
                    getTaskBucket(rootIdentity.serviceId, getRootStatus(order), order?.ScheduledAt),
                    ...related.map((item) => {
                        const identity = resolveRelatedServiceId(item)
                        return getTaskBucket(identity.serviceId, getRelatedItemStatus(item), item?.ScheduledAt)
                    }),
                ]
                if (!buckets.includes(statusFilter as TaskBucket)) return false
            }
            return true
        })
    }, [visibleOrders, search, serviceFilter, statusFilter])

    const clientGroups = useMemo(() => groupOrdersByClient(getUniqueOrdersById(filteredOrders)), [filteredOrders])
    const totalLandlordCount = useMemo(() => (
        allUsers.length > 0 ? countLandlordCapableUsers(allUsers) : null
    ), [allUsers])
    const orderStats = useMemo(() => buildLandlordServiceOrderStatsFromGroups(clientGroups, totalLandlordCount ?? 0), [clientGroups, totalLandlordCount])
    const hasActiveFilters = search.trim().length > 0 || statusFilter !== 'all' || serviceFilter !== 'all'

    const updateDraft = (key: string, source: ServiceDraft, patch: Partial<ServiceDraft>): void => {
        setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] ?? source), ...patch } }))
        setResponsibilities((prev) => ({ ...prev, [key]: prev[key] ?? buildDefaultResponsibilityChecks() }))
    }

    const updateResponsibility = (key: string, checks: ResponsibilityChecks): void => {
        setResponsibilities((prev) => ({ ...prev, [key]: checks }))
    }

    const resetDraft = (key: string): void => {
        setDrafts((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
        })
        setResponsibilities((prev) => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const setCollapsed = (key: string, collapsed: boolean): void => {
        setCollapsedKeys((prev) => ({ ...prev, [key]: collapsed }))
    }

    const toggleUserCollapsed = (userKey: string): void => {
        setCollapsedUserIds((prev) => {
            const next = new Set(prev)
            if (next.has(userKey)) next.delete(userKey)
            else next.add(userKey)
            return next
        })
    }

    const saveRelatedService = async (order: any, item: any, index: number, key: string): Promise<void> => {
        const identity = resolveRelatedServiceId(item)
        const source = getSourceDraft(item)
        const draft = drafts[key] ?? source
        const allowedStatuses = getAdminAllowedStatuses(identity.serviceId)
        const requiresSchedule = isAdminScheduledStatus(identity.serviceId, draft.status)

        if (!identity.isKnown || !isAdminRelatedServiceEditable(identity.serviceId)) {
            setError(`Service "${identity.serviceId}" is read-only or unknown.`)
            return
        }
        if (!allowedStatuses.includes(draft.status)) {
            setError(`Status "${draft.status}" is not allowed for ${identity.serviceId}.`)
            return
        }
        if (requiresSchedule && draft.scheduledAtInput.trim().length === 0) {
            setError('ScheduledAt is required for this status.')
            return
        }
        if (!isResponsibilityConfirmed(responsibilities[key] ?? buildDefaultResponsibilityChecks())) {
            setError('Complete responsibility confirmation before saving.')
            return
        }

        const patch = buildRelatedPatchFromDraft(draft, identity.serviceId)
        const body = buildOrderRelatedServiceUpdateBody({ order, serviceId: identity.serviceId, itemIndex: index, patch })
        assertSafeOrderRelatedServiceBody(body)

        const confirmation = await Swal.fire({
            title: 'Confirm related service update',
            html: [
                `<div class="text-start">Order: <b>${String(order.id)}</b></div>`,
                `<div class="text-start">Service: <b>${identity.serviceId}</b> - ${resolveServiceTitle(identity.serviceId, item)}</div>`,
                `<div class="text-start">Old status: <b>${source.status || 'Not set'}</b></div>`,
                `<div class="text-start">New status: <b>${draft.status}</b></div>`,
                requiresSchedule ? `<div class="text-start">ScheduledAt: <b>${toScheduledIso(draft.scheduledAtInput)}</b></div>` : '',
                `<div class="text-start">Notify: <b>${String(source.notify)} -> ${String(draft.notify)}</b></div>`,
            ].join(''),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Save',
        })
        if (!confirmation.isConfirmed) return

        setSavingKey(key)
        setError('')
        try {
            await updateOrderRelatedService({ order, serviceId: identity.serviceId, itemIndex: index, patch })
            await loadOrders()
            resetDraft(key)
        } catch (e: any) {
            setError(e?.message ?? 'Failed to save related service.')
        } finally {
            setSavingKey('')
        }
    }

    const saveRootService = async (order: any, key: string): Promise<void> => {
        const identity = resolveRootServiceIdFromOrder(order)
        const source = getRootSourceDraft(order)
        const draft = drafts[key] ?? source
        const allowedStatuses = getAdminAllowedStatuses(identity.serviceId)
        const requiresSchedule = isAdminScheduledStatus(identity.serviceId, draft.status)

        if (!canEditRootServiceStatus(order, identity)) {
            setError(`Root service "${identity.serviceId}" is read-only or unknown.`)
            return
        }
        if (!allowedStatuses.includes(draft.status)) {
            setError(`Status "${draft.status}" is not allowed for ${identity.serviceId}.`)
            return
        }
        if (requiresSchedule && draft.scheduledAtInput.trim().length === 0) {
            setError('ScheduledAt is required for this status.')
            return
        }
        if (!isResponsibilityConfirmed(responsibilities[key] ?? buildDefaultResponsibilityChecks())) {
            setError('Complete responsibility confirmation before saving.')
            return
        }

        const patch = buildRootPatchFromDraft(order, draft, identity.serviceId)
        const body = buildRootOrderServiceStatusUpdateBody({ order, patch })
        assertSafeRootOrderServiceStatusBody(body)

        const confirmation = await Swal.fire({
            title: 'Confirm root service update',
            html: [
                `<div class="text-start">Order: <b>${String(order.id)}</b></div>`,
                `<div class="text-start">Service: <b>${identity.serviceId}</b> - ${resolveServiceTitle(identity.serviceId, toArray<any>(order?.Service)[0])}</div>`,
                `<div class="text-start">Old status: <b>${source.status || 'Not set'}</b></div>`,
                `<div class="text-start">New status: <b>${draft.status}</b></div>`,
                requiresSchedule ? `<div class="text-start">ScheduledAt: <b>${toScheduledIso(draft.scheduledAtInput)}</b></div>` : '',
                `<div class="text-start">Notify: <b>${String(source.notify)} -> ${String(draft.notify)}</b></div>`,
            ].join(''),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Save',
        })
        if (!confirmation.isConfirmed) return

        setSavingKey(key)
        setError('')
        try {
            await updateRootOrderServiceStatus({ order, patch })
            await loadOrders()
            resetDraft(key)
        } catch (e: any) {
            setError(e?.message ?? 'Failed to save root service.')
        } finally {
            setSavingKey('')
        }
    }

    if (!isAdmin) return <Navigate to="/landlord/menu" replace />

    return (
        <div className="container-fluid py-6 lso-page">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-5">
                <div>
                    <h1 className="fs-2 fw-bold mb-1">Landlord Service Orders</h1>
                    <div className="text-gray-600 fs-7">Operational status control for landlord/client service orders.</div>
                </div>
                <button className="btn btn-sm btn-light-primary" type="button" onClick={() => { void loadOrders() }} disabled={loadStatus === 'loading'}>
                    {loadStatus === 'loading' ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            <section className="lso-stats-grid mb-5" aria-label="Landlord service order statistics">
                <article className="lso-stat-card">
                    <span className="lso-stat-label">Total Landlords</span>
                    <strong className="lso-stat-value">
                        {totalLandlordCount ?? '—'}
                    </strong>
                    <div className="lso-stat-lines">
                        <small>With orders: {visibleLandlordCount}</small>
                        {totalLandlordCount == null && <small>Total is unavailable until admin users are loaded.</small>}
                        {hasActiveFilters && <small>Visible after filters: {orderStats.landlordsWithOrders}</small>}
                    </div>
                </article>
                <article className="lso-stat-card">
                    <span className="lso-stat-label">Landlords With Orders</span>
                    <strong className="lso-stat-value">{orderStats.landlordsWithOrders}</strong>
                    <div className="lso-stat-lines">
                        <small>Visible orders: {orderStats.visibleOrders}</small>
                        {hasActiveFilters && <small>Filtered landlords: {orderStats.landlordsWithOrders}</small>}
                    </div>
                </article>
                <article className="lso-stat-card">
                    <span className="lso-stat-label">Total Orders</span>
                    <strong className="lso-stat-value">{orderStats.visibleOrders}</strong>
                    <div className="lso-stat-lines">
                        <small>Active: {orderStats.active}</small>
                        <small>Scheduled: {orderStats.scheduled}</small>
                        <small>Closed: {orderStats.terminal}</small>
                    </div>
                </article>

            </section>

            <div className="card card-flush shadow-sm mb-5 lso-filter-card">
                <div className="card-body py-4">
                    <div className="row g-3">
                        <div className="col-12 col-lg-5">
                            <input className="form-control" placeholder="Search client, email, phone, order, unit, service, status" value={search} onChange={(event) => setSearch(event.target.value)} />
                        </div>
                        <div className="col-12 col-sm-6 col-lg-3">
                            <select className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                                <option value="all">All statuses</option>
                                <option value="active">Active</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="canceled">Canceled</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                            <select className="form-select" value={serviceFilter} onChange={(event) => setServiceFilter(event.target.value)}>
                                <option value="all">All services</option>
                                {serviceFilterOptions.map((service) => <option key={service.id} value={service.id}>{service.title}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="fs-8 text-gray-600 mt-3">{orderStats.landlordsWithOrders} landlords · {orderStats.visibleOrders} visible orders</div>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="lso-client-list">
                {clientGroups.map((group) => (
                    <ClientOrderGroupCard
                        key={group.summary.key}
                        group={group}
                        drafts={drafts}
                        responsibilities={responsibilities}
                        savingKey={savingKey}
                        collapsedKeys={collapsedKeys}
                        collapsedUserIds={collapsedUserIds}
                        onSetCollapsed={setCollapsed}
                        onToggleUser={toggleUserCollapsed}
                        onDraftChange={updateDraft}
                        onResponsibilityChange={updateResponsibility}
                        onResetDraft={resetDraft}
                        onSaveRoot={(order, key) => { void saveRootService(order, key) }}
                        onSaveRelated={(order, item, index, key) => { void saveRelatedService(order, item, index, key) }}
                    />
                ))}
                {clientGroups.length === 0 && (
                    <div className="card card-flush shadow-sm border">
                        <div className="card-body text-center text-gray-600 py-10">
                            {loadStatus === 'loading' ? 'Loading orders...' : 'No landlord service orders found.'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
